import { useMemo } from "react"
import PropTypes from "prop-types"
import { Paper } from "@mui/material"
import { chain, map, find, keys, omitBy } from "lodash"
import Table from "components/material/table"
import { DATASET, UUID, ROLE } from "helpers/shapes"
import { makeDatasetElementRender } from "helpers/datasets"
import UpsellTabs, { UPSELL_TABS_SHAPE } from "components/material/upsell-tabs"
import { isAllAccessTeam } from "helpers/permissions/teams"

export default function TeamDatasetEntitlements({ account, role, teamAlias, entitlements, datasets, update, tabs }) {
  const hasWriteAccess = role === "admin" && !isAllAccessTeam(teamAlias)
  // Cannot reach this page if the entitlements are not enabled
  const entitlementsEnabled = true

  const optionLabels = useMemo(() => chain(datasets)
    .orderBy("name", "asc")
    .reduce((datasetsById, dataset) => ({
      ...datasetsById,
      [dataset.id]: dataset.name,
    }), {})
    .value(),
  [datasets])

  const availableOptionLabels = useMemo(() =>
    omitBy(optionLabels, (_, id) => find(entitlements, entitlement => entitlement.id === id)),
  [entitlements, optionLabels])

  const addForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onAdd(data, cb) {
      const query = `mutation ($teamIds: [IdOrAlias]){
        ${map(data.datasets, (datasetId, index) => `dataset${index}: grantDatasetAccess(id: "${datasetId}", teamIds: $teamIds) { id }`)}
      }`

      const variables = { teamIds: [teamAlias] }

      update(query, variables, account, {
        reloadData: ["teamDatasetEntitlements", "memberDatasetEntitlements", "userDatasets", "models", "datasetEntitlements", "datasetTeamEntitlements"],
      }, cb)
    }

    return {
      title: "Grant Dataset Access",
      submitText: "Grant access",
      track: {
        type: "dataset-entitlements-grant-datasets-team",
        payload: ({ datasets }) => ({
          datasets,
          teamAlias,
        }),
      },
      onSubmit: onAdd,
      fields:
      [{
        id: "datasets",
        label: "Datasets",
        type: "multi",
        options: keys(availableOptionLabels),
        optionLabels: availableOptionLabels,
        validations: {
          isRequired: true,
        },
      }],
    }
  }, [hasWriteAccess, availableOptionLabels, teamAlias, update, account])

  const deleteForm = useMemo(() => {
    if (!hasWriteAccess) { return null }

    function onDelete(data, cb) {
      const query = `mutation ($teamIds: [IdOrAlias]){
        ${map(data, (dataset, index) => `dataset${index}: revokeDatasetAccess(id: "${dataset.id}", teamIds: $teamIds) { id }`)}
      }`

      const variables = { teamIds: [teamAlias] }

      update(query, variables, account, {
        reloadData: ["teamDatasetEntitlements", "memberDatasetEntitlements", "userDatasets", "models", "datasetEntitlements", "datasetTeamEntitlements"],
      }, cb)
    }

    return {
      type: "dataset-entitlement",
      track: {
        type: "dataset-entitlements-revoke-datasets-team",
        payload: (dataset) => ({
          dataset: dataset.id,
          teamAlias,
        }),
      },
      isEnabled: () => hasWriteAccess,
      renderItem: dataset => <li key={dataset.id}>{dataset.name}</li>,
      onSubmit: onDelete,
    }
  }, [account, hasWriteAccess, teamAlias, update])

  // Same function for all table elements
  const getNameElement = makeDatasetElementRender(account, role, entitlementsEnabled)

  return <div className="modules_team-dataset-entitlements">
    <UpsellTabs id="team-dataset-entitlements" tabs={tabs} />
    <Paper className="content" square>
      <Table
        title="Team dataset entitlements"
        idField="id"
        addForm={addForm}
        editForm={null}
        deleteForm={deleteForm}
        data={entitlements}
        columns={[{
          name: "Name",
          id: "name",
          render: getNameElement,
        }]}
      />
    </Paper>
  </div>
}

TeamDatasetEntitlements.propTypes = {
  account: PropTypes.string.isRequired,
  teamAlias: PropTypes.string.isRequired,
  userId: UUID,
  role: ROLE.isRequired,
  entitlements: PropTypes.arrayOf(DATASET),
  datasets: PropTypes.arrayOf(DATASET).isRequired,
  update: PropTypes.func.isRequired,
  tabs: UPSELL_TABS_SHAPE,
}
